import { useMemo } from "react";

const DEFAULT_RECORD_KEY = "entries";

/**
 * Builds form default values from query data by reducing an array to a record keyed by id.
 * Use with useFormTableCellDirty when the form shape is { [recordKey]: Record<id, T> }.
 *
 * @param T - Item type from API (must have `id`)
 * @param K - Record key (e.g. "entries", "costs"). Defaults to "entries".
 * @param TForm - Form type for the return value; when provided, eliminates need for call-site assertions
 */
export function useFormDefaultValuesFromQuery<
  T extends { id: string },
  K extends string = typeof DEFAULT_RECORD_KEY,
  TForm extends Record<K, Record<string, unknown>> = {
    [P in K]: Record<string, T>;
  },
>(data: T[] | undefined, recordKey?: K): TForm {
  const key = (recordKey ?? DEFAULT_RECORD_KEY) as K;
  return useMemo(
    () =>
      ({
        [key]:
          data?.reduce(
            (acc, item) => ({ ...acc, [item.id]: item }),
            {} as Record<string, T>,
          ) ?? {},
      }) as TForm,
    [data, key],
  );
}
